import CloseIcon from '@mui/icons-material/Close';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {JIRA_AMENDMENT_URL} from '@account/config';
import {JIRA_KEY_RE, formatErrorMsg, getError} from '@account/utils/errors';
import {adjustTermQuota} from '@account/api';
import {renderQuota} from '@account/utils/quota';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTermBalance} from '@hooks/data/products/useTermBalance';

const AdjustTermQuotaModal = ({open, onClose, productAccessId, term}) => {
  const queryClient = useQueryClient();
  const [deltaRaw, setDeltaRaw] = useState('');
  const [supportTicketRef, setSupportTicketRef] = useState('');
  const [notes, setNotes] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const {
    data: balanceData,
    isLoading: balanceLoading,
    isError: balanceError,
  } = useTermBalance(productAccessId, term.id, {enabled: open});
  const balance = balanceData?.balance ?? null;

  const delta = deltaRaw === '' ? null : Number(deltaRaw);
  const isInvalidNumber = delta !== null && isNaN(delta);
  const isFloat = delta !== null && !isNaN(delta) && !Number.isInteger(delta);
  const isZero = delta === 0;
  const isNegative = delta !== null && delta < 0;
  const exceedsBalance =
    isNegative && balance !== null && Math.abs(delta) > balance;

  const canSubmit =
    delta !== null &&
    !isInvalidNumber &&
    !isFloat &&
    !isZero &&
    !exceedsBalance &&
    !(isNegative && balance === null) &&
    JIRA_KEY_RE.test(supportTicketRef.trim()) &&
    !!notes.trim();

  const {mutate, isLoading} = useMutation(
    () =>
      adjustTermQuota(productAccessId, term.id, {
        delta_amount: delta,
        quota_units: term.quota_units,
        support_ticket_ref: supportTicketRef.trim(),
        notes: notes.trim(),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccessTerms', productAccessId]);
        queryClient.invalidateQueries(['productAccess', productAccessId]);
        queryClient.invalidateQueries([
          'termBalance',
          productAccessId,
          term.id,
        ]);
        onClose();
      },
      onError: err => {
        setErrorMsg(formatErrorMsg(getError(err)));
      },
    }
  );

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adjust quota
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{position: 'absolute', right: 8, top: 8}}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{mt: 1}}>
            <Typography variant="body2" color="text.secondary">
              Adjusts quota within this access period only — does not affect
              other periods or the overall product allocation.
            </Typography>

            <TextField
              label="Delta amount"
              type="number"
              value={deltaRaw}
              onChange={e => {
                setDeltaRaw(e.target.value);
                setErrorMsg(null);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {term.quota_units}
                  </InputAdornment>
                ),
              }}
              error={isInvalidNumber || isFloat}
              helperText={
                isInvalidNumber
                  ? 'Enter a valid number.'
                  : isFloat
                    ? 'Enter a whole number — fractional quota is not allowed.'
                    : 'Use a negative number to remove quota.'
              }
              fullWidth
              required
            />

            <Box
              sx={theme => ({
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
                bgcolor: 'grey.50',
              })}
            >
              <Typography variant="subtitle2" display="block" mb={1}>
                Quota preview
              </Typography>

              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Current balance
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      balance === null && !balanceLoading
                        ? 'text.secondary'
                        : 'text.primary'
                    }
                  >
                    {balanceLoading ? (
                      <CircularProgress size={12} />
                    ) : balanceError ? (
                      'Error loading balance'
                    ) : balance !== null ? (
                      renderQuota(balance, term.quota_units)
                    ) : (
                      'Unavailable'
                    )}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Change
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      delta === null || isZero
                        ? 'text.primary'
                        : delta > 0
                          ? 'success.main'
                          : 'error.main'
                    }
                  >
                    {delta === null || isZero
                      ? '—'
                      : `${delta > 0 ? '+' : ''}${delta.toLocaleString()} ${term.quota_units}`}
                  </Typography>
                </Stack>

                <Divider sx={{my: 0.5}} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>
                    After adjustment
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {balance !== null && delta !== null && !isZero
                      ? renderQuota(balance + delta, term.quota_units)
                      : '—'}
                  </Typography>
                </Stack>
              </Stack>

              {isZero && (
                <Alert severity="info" sx={{mt: 1}} variant="outlined">
                  Delta is zero — no change will be made.
                </Alert>
              )}

              {exceedsBalance && (
                <Alert severity="error" sx={{mt: 1}} variant="outlined">
                  Amount exceeds the available balance of{' '}
                  {renderQuota(balance, term.quota_units)}.
                </Alert>
              )}

              {isNegative && balance === null && !balanceLoading && (
                <Alert severity="error" sx={{mt: 1}} variant="outlined">
                  Current balance is unavailable, so quota cannot be removed.
                  Try again once the balance loads.
                </Alert>
              )}
            </Box>

            <TextField
              label="Support ticket ref"
              value={supportTicketRef}
              onChange={e => setSupportTicketRef(e.target.value)}
              placeholder="CGO-1234"
              error={
                !!supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim())
              }
              helperText={
                supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim()) ? (
                  'Must match format PROJECT-1234 (e.g. CGO-1234)'
                ) : supportTicketRef.trim() ? (
                  'Format: PROJECT-1234 (e.g. CGO-1234)'
                ) : (
                  <span>
                    Need approval first?{' '}
                    <Link
                      href={JIRA_AMENDMENT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Request amendment approval
                    </Link>
                  </span>
                )
              }
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
            />

            {errorMsg && (
              <Typography color="error" variant="body2">
                {errorMsg}
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => mutate()}
            variant="contained"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Saving…' : 'Apply change'}
          </Button>
        </DialogActions>
      </Dialog>

      <Notifier open={isLoading} message="Adjusting quota…" progress />
    </>
  );
};

AdjustTermQuotaModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  productAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  term: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    quota_units: PropTypes.string,
  }).isRequired,
};

export default AdjustTermQuotaModal;
